import React from 'react'
import moment from 'moment'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { withFirebase } from '../Fb'
import { effectiveCancellationEnd, hasOwnCancellationEnd } from '../utils/activityCancellationDeadline'
import { formatCollision } from '../utils/activityReservations'

// Confirms an activity template dropped onto the activity calendar
// (docs/features/activity-templates.md). "Luo" saves the activity as built from the template,
// books the template's default courts for its time, and closes; "Muokkaa ennen tallennusta"
// hands the same activity to the creation wizard instead.
//
// Courts are booked only when that can work: the feature is on, the template names a payer
// (reservationpayers - a coach cannot hold a booking, so it is not the organiser) and none of
// the courts is taken. Otherwise the activity is still created and the reason is shown, so the
// courts can be booked from Varaukset later.

const when = (value) => (value ? moment.utc(value).format('dd D.M.YYYY HH:mm') : '')

function ActivityTemplateDropDialog({ open, template, activity, resources, attaching, firebase, onClose, onCreated, onEdit, onRecurrent }) {
  const [conflicts, setConflicts] = React.useState([])
  const [checking, setChecking] = React.useState(false)
  const [checkError, setCheckError] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState(null)

  const courts = React.useMemo(() => {
    if (!attaching || !template) return []
    return (template.resourceids || []).map((id) => (resources || []).find((r) => r.id === id)).filter(Boolean)
  }, [attaching, template, resources])
  const payers = (template && template.reservationpayers) || []

  React.useEffect(() => {
    setConflicts([])
    setCheckError(null)
    setSaveError(null)
    if (!open || !activity || courts.length === 0) return
    let cancelled = false
    setChecking(true)
    firebase.checkResourceConflicts(activity.start, activity.end, courts).then((result) => {
      if (cancelled) return
      setChecking(false)
      if (!result || result.error) {
        setCheckError('Kenttien päällekkäisyyksien tarkistus epäonnistui.')
        return
      }
      setConflicts(result.filter((row) => row.isconflict))
    })
    return () => {
      cancelled = true
    }
  }, [open, activity, courts, firebase])

  if (!activity || !template) return null

  const courtProblem =
    courts.length === 0
      ? null
      : payers.length === 0
      ? 'Pohjassa ei ole kenttävarausten maksajaa. Kenttiä ei varata; lisää maksaja pohjaan tai varaa kentät aktiviteetin Varaukset-välilehdeltä.'
      : checkError
      ? checkError + ' Kenttiä ei varata.'
      : conflicts.length > 0
      ? 'Kentät ovat varattuina tähän aikaan, joten niitä ei varata: ' +
        conflicts.map((c) => (c.resource ? c.resource.displayName : '') + ' ' + formatCollision(c.collision)).join(', ')
      : null
  const bookCourts = courts.length > 0 && !courtProblem && !checking

  const create = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      // createActivity fills in the question ids and creator fields on this object, so the
      // follow-up update reuses it rather than assigning new ids
      const saved = { ...activity }
      const ref = await firebase.createActivity(saved)
      let bookingErrors = []
      if (bookCourts && ref && ref.id) {
        const result = await firebase.createActivityReservations({ ...saved, id: ref.id }, courts, payers, {
          cost: Number(template.reservationcost) || 0,
        })
        bookingErrors = result.errors
        if (result.created.length > 0) {
          await firebase.updateActivity({ ...saved, id: ref.id, reservations: result.created })
        }
      }
      setSaving(false)
      onCreated(ref && ref.id, bookingErrors)
    } catch (error) {
      console.error('Creating from template failed', error)
      setSaving(false)
      setSaveError('Aktiviteetin luonti epäonnistui.')
    }
  }

  const ownDeadline = hasOwnCancellationEnd(activity)
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Luodaanko aktiviteetti pohjasta?</DialogTitle>
      <DialogContent>
        <Typography variant='h6'>{activity.name && activity.name.fi}</Typography>
        <Typography gutterBottom>
          {when(activity.start)}–{moment.utc(activity.end).format('HH:mm')}
        </Typography>
        <Typography variant='body2'>
          Ilmoittautuminen {when(activity.entrystart)} – {when(activity.entryend)}
        </Typography>
        <Typography variant='body2'>
          Peruutus päättyy {when(effectiveCancellationEnd(activity))}
          {ownDeadline ? '' : ' (sama kuin ilmoittautuminen)'}
        </Typography>
        <Typography variant='body2' gutterBottom>
          {activity.state === 'published' ? 'Julkaistaan heti' : 'Luodaan luonnoksena'}
        </Typography>
        {courts.length > 0 ? (
          <Typography variant='body2'>
            Kentät: {courts.map((c) => c.displayName || c.name).join(', ')}
            {payers.length > 0
              ? ' · maksaja ' + payers.map((p) => (p.businessId ? p.name : (p.firstname || '') + ' ' + (p.lastname || '')) + (p.email ? ' (' + p.email + ')' : '')).join(', ')
              : ''}
            {checking ? <CircularProgress size={12} sx={{ ml: 1 }} /> : null}
          </Typography>
        ) : null}
        {courtProblem ? (
          <Alert severity='warning' sx={{ mt: 1 }}>
            {courtProblem}
          </Alert>
        ) : null}
        {courts.length > 0 ? (
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
            Muokkaa ennen tallennusta -polulla kenttiä ei varata; ne varataan aktiviteetin Varaukset-välilehdeltä.
            Toistuvalle sarjalle kenttiä ei varata automaattisesti.
          </Typography>
        ) : null}
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
          Luo toistuvana: viikoittainen sarja alkaa tästä päivästä samaan aikaan; loppupäivä valitaan seuraavaksi.
        </Typography>
        {saveError ? (
          <Alert severity='error' sx={{ mt: 1 }}>
            {saveError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Peruuta
        </Button>
        <Button onClick={onRecurrent} disabled={saving}>
          Luo toistuvana
        </Button>
        <Button onClick={onEdit} disabled={saving}>
          Muokkaa ennen tallennusta
        </Button>
        <Button onClick={create} variant='contained' color='primary' disabled={saving || checking}>
          {saving ? <CircularProgress size={16} color='inherit' /> : 'Luo'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(ActivityTemplateDropDialog)
